import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

const SocialMediaAdmin = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768;

  // State Variables
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [statistics, setStatistics] = useState({
    totalPosts: 0,
    totalLikes: 0,
    publicPosts: 0,
    groupPosts: 0
  });
  const [socialSettings, setSocialSettings] = useState({
    postsEnabled: true,
    commentsEnabled: true,
    likesEnabled: true
  });
  const [overlayVisible, setOverlayVisible] = useState(false);

  // NEW: Create Post State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPostLoading, setCreatePostLoading] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    isGroupSpecific: false,
    selectedImage: null
  });

  // Initialize component
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    
    initializeData();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const initializeData = async () => {
    await Promise.all([
      loadPosts(),
      loadSettings(),
      loadStatistics()
    ]);
  };

  // Load posts from API
  const loadPosts = async (page = 0, search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await localforage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        size: '20',
        sortBy: 'createdAt',
        sortDir: 'desc'
      });

      if (search) {
        params.append('search', search);
      }

      console.log('Loading posts from:', `${BASE_URL}/api/social/admin/posts?${params}`);

      const response = await fetch(`${BASE_URL}/api/social/admin/posts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Posts response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Posts loaded:', data.posts?.length || 0);
        setPosts(data.posts);
        setCurrentPage(data.currentPage);
        setTotalPages(data.totalPages);
        setTotalItems(data.totalItems);
      } else if (response.status === 401) {
        setError('Session expired. Please login again.');
      } else if (response.status === 403) {
        setError('Access denied. Admin permissions required.');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to load posts');
      }
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load social media settings
  const loadSettings = async () => {
    try {
      const token = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/social/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const settings = await response.json();
        setSocialSettings(settings);
        console.log('Social settings loaded:', settings);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const token = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/social/admin/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const stats = await response.json();
        setStatistics(stats);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  // NEW: Create Post Function
  const createPost = async () => {
    if (!newPost.title.trim()) {
      setError('Please enter a title for your post');
      return;
    }

    try {
      setCreatePostLoading(true);
      setError(null);
      
      const token = await localforage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      console.log('Creating post with data:', {
        title: newPost.title,
        description: newPost.description,
        isGroupSpecific: newPost.isGroupSpecific,
        hasImage: !!newPost.selectedImage
      });

      // Test 1: Simple JSON Post (no image)
      if (!newPost.selectedImage) {
        console.log('Testing JSON post creation...');
        
        const response = await fetch(`${BASE_URL}/api/social/posts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: newPost.title.trim(),
            description: newPost.description.trim(),
            isGroupSpecific: newPost.isGroupSpecific
          })
        });

        console.log('JSON Post response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('JSON Post created successfully:', result);
          setSuccessMessage('✅ JSON Post created successfully!');
          setShowCreateModal(false);
          setNewPost({ title: '', description: '', isGroupSpecific: false, selectedImage: null });
          await loadPosts(0, searchQuery);
          await loadStatistics();
        } else {
          const errorText = await response.text();
          console.error('JSON Post error:', errorText);
          setError(`JSON Post failed: ${response.status} - ${errorText}`);
        }
      } 
      // Test 2: FormData Post (with image)
      else {
        console.log('Testing FormData post creation...');
        
        const formData = new FormData();
        formData.append('title', newPost.title.trim());
        if (newPost.description.trim()) {
          formData.append('description', newPost.description.trim());
        }
        formData.append('isGroupSpecific', newPost.isGroupSpecific.toString());
        
        if (newPost.selectedImage) {
          formData.append('image', newPost.selectedImage);
        }

        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
          console.log(key, typeof value === 'object' ? 'File' : value);
        }

        const response = await fetch(`${BASE_URL}/api/social/posts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // DO NOT set Content-Type for FormData
          },
          body: formData
        });

        console.log('FormData Post response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('FormData Post created successfully:', result);
          setSuccessMessage('✅ FormData Post created successfully!');
          setShowCreateModal(false);
          setNewPost({ title: '', description: '', isGroupSpecific: false, selectedImage: null });
          await loadPosts(0, searchQuery);
          await loadStatistics();
        } else {
          const errorText = await response.text();
          console.error('FormData Post error:', errorText);
          setError(`FormData Post failed: ${response.status} - ${errorText}`);
        }
      }

    } catch (error) {
      console.error('Error creating post:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setCreatePostLoading(false);
    }
  };

  // NEW: Handle Image Selection
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file too large. Please select an image under 5MB');
        return;
      }

      setNewPost(prev => ({ ...prev, selectedImage: file }));
      console.log('Image selected:', file.name, file.type, file.size);
    }
  };

  // Update social media settings
  const updateSettings = async (newSettings) => {
    try {
      setError(null);
      const token = await localforage.getItem('token');
      
      const params = new URLSearchParams();
      params.append('postsEnabled', newSettings.postsEnabled);
      params.append('commentsEnabled', newSettings.commentsEnabled);
      params.append('likesEnabled', newSettings.likesEnabled);

      const response = await fetch(`${BASE_URL}/api/social/admin/settings?${params}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setSocialSettings(updatedSettings);
        setSuccessMessage('Settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Network error: ' + err.message);
    }
  };

  // Delete post
  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      const token = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/social/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setPosts(posts.filter(post => post.id !== postId));
        setSuccessMessage('Post deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Reload statistics
        loadStatistics();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Network error: ' + err.message);
    }
  };

  // Export images
  const exportImages = async () => {
    try {
      setError(null);
      const token = await localforage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/social/admin/export-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(`Export prepared: ${data.totalImages} images available for download`);
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // In a real implementation, you would trigger the download here
        console.log('Image paths:', data.imagePaths);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to export images');
      }
    } catch (err) {
      console.error('Error exporting images:', err);
      setError('Network error: ' + err.message);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounced search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(0);
      loadPosts(0, query);
    }, 500);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadPosts(newPage, searchQuery);
  };

  // Toggle post expansion
  const toggleExpanded = (postId) => {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      setOverlayVisible(false);
    } else {
      setExpandedPostId(postId);
      setOverlayVisible(true);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusColor = (isGroupSpecific) => {
    return isGroupSpecific ? '#3b82f6' : '#10b981';
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccessMessage('');
  };

  const styles = {
    container: {
      padding: '32px',
      maxWidth: 'auto',
      margin: '0 auto',
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      marginLeft: isMobile ? "-20px" : 'auto',
      alignItems: "center",
      borderRadius: "20px",
      width: isMobile ? "93%" : 'auto',
      marginTop: isMobile ? "0px" : 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      width: isMobile ? "20px" : 'auto',
    },
    headerLeft: {
      flex: 1,
      marginLeft: isMobile ? "32px" : 'auto',
      marginTop: isMobile ? "-126px" : "0",
      width: isMobile ? "200px" : 'auto',
    },
    title: {
      fontSize: '32px',
      fontWeight: '600',
      color: '#1e293b',
      margin: '0 0 16px 0',
      textAlign: "center"
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      margin: '0 0 24px 0',
      textAlign: "center"
    },
    controls: {
      display: 'flex',
      gap: '16px',
      marginLeft: isMobile ? "auto" : 'auto',
      alignItems: "center",
      flexDirection: isMobile ? "column" : "row",
    },
    searchInput: {
      padding: '10px 16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      outline: 'none',
      width: isMobile ? "255px" : '300px',
    },
    
    // Messages
    messageContainer: {
      marginBottom: '24px',
    },
    errorMessage: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '16px',
      color: '#dc2626',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    successMessage: {
      backgroundColor: '#f0fdf4',
      border: '1px solid #bbf7d0',
      borderRadius: '8px',
      padding: '16px',
      color: '#16a34a',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    messageCloseButton: {
      background: 'none',
      border: 'none',
      fontSize: '18px',
      cursor: 'pointer',
      marginLeft: '12px',
    },
    
    // Settings Panel
    settingsPanel: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
    },
    settingsHeader: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    settingsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: '16px',
      marginBottom: '16px',
    },
    settingItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    settingLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
    },
    toggle: {
      width: '44px',
      height: '24px',
      backgroundColor: '#e5e7eb',
      borderRadius: '12px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    toggleActive: {
      backgroundColor: '#10b981',
    },
    toggleButton: {
      width: '20px',
      height: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      position: 'absolute',
      top: '2px',
      left: '2px',
      transition: 'transform 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    },
    toggleButtonActive: {
      transform: 'translateX(20px)',
    },
    actionsRow: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      flexDirection: isMobile ? 'column' : 'row',
    },
    actionButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    actionButtonPrimary: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: '1px solid #3b82f6',
    },
    actionButtonSuccess: {
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: '1px solid #10b981',
    },
    actionButtonDanger: {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: '1px solid #ef4444',
    },
    
    // Stats Row
    statsRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: '16px',
      marginBottom: '24px',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
      textAlign: 'center',
    },
    statNumber: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px',
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500',
    },
    
    // Create Post Modal
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      width: '90%',
      maxWidth: '500px',
      maxHeight: '80vh',
      overflow: 'auto',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
    },
    modalCloseButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#64748b',
    },
    formGroup: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    fileInput: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      backgroundColor: '#f8fafc',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    checkboxInput: {
      width: '16px',
      height: '16px',
    },
    selectedImage: {
      width: '100%',
      maxHeight: '200px',
      objectFit: 'cover',
      borderRadius: '6px',
      marginTop: '8px',
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '24px',
    },
    
    // Posts List (keeping existing styles)
    postsGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    postCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      transition: 'all 0.2s',
      position: 'relative',
    },
    postCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    },
    postHeader: {
      padding: '20px',
      borderBottom: '1px solid #f1f5f9',
      cursor: 'pointer',
    },
    postMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    postTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0,
      flex: 1,
      marginRight: '16px',
    },
    postActions: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    actionIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontSize: '14px',
    },
    deleteIcon: {
      color: '#ef4444',
      borderColor: '#fecaca',
      backgroundColor: '#fef2f2',
    },
    expandIcon: {
      color: '#6b7280',
    },
    postInfo: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      fontSize: '14px',
      color: '#64748b',
      flexWrap: 'wrap',
    },
    authorInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      color: '#ffffff',
    },
    postStats: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      color: '#64748b',
    },
    
    // Expanded Content
    expandedContent: {
      padding: '20px',
      borderTop: '1px solid #f1f5f9',
      backgroundColor: '#f8fafc',
    },
    postDescription: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.6',
      marginBottom: '16px',
    },
    postImage: {
      width: '100%',
      maxWidth: '400px',
      height: 'auto',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    postDetails: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '16px',
    },
    detailItem: {
      padding: '12px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    detailLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#64748b',
      marginBottom: '4px',
    },
    detailValue: {
      fontSize: '14px',
      color: '#1e293b',
    },
    
    // Pagination
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginTop: '24px',
      flexWrap: 'wrap',
    },
    pageButton: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s',
    },
    pageButtonActive: {
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      borderColor: '#3b82f6',
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Loading and Error States
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      flexDirection: 'column',
      gap: '16px',
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '4px solid #f3f4f6',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    
    // Overlay
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.1)',
      zIndex: 5,
      display: overlayVisible ? 'block' : 'none',
    },
  };

  return (
    <div style={styles.container}>
      {/* Overlay */}
      <div 
        style={styles.overlay}
        onClick={() => {
          setExpandedPostId(null);
          setOverlayVisible(false);
        }}
      />

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Social Media Management</h1>
          <p style={styles.subtitle}>Manage posts, settings, and monitor community activity</p>
        </div>
      </div>

      {/* Messages */}
      {(error || successMessage) && (
        <div style={styles.messageContainer}>
          {error && (
            <div style={styles.errorMessage}>
              <span>⚠️ {error}</span>
              <button 
                style={styles.messageCloseButton}
                onClick={clearMessages}
              >
                ×
              </button>
            </div>
          )}
          {successMessage && (
            <div style={styles.successMessage}>
              <span>{successMessage}</span>
              <button 
                style={styles.messageCloseButton}
                onClick={clearMessages}
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* Settings Panel */}
      <div style={styles.settingsPanel}>
        <h2 style={styles.settingsHeader}>
          ⚙️ Social Media Settings
        </h2>
        
        <div style={styles.settingsGrid}>
          <div style={styles.settingItem}>
            <span style={styles.settingLabel}>Posts Enabled</span>
            <div 
              style={{
                ...styles.toggle,
                ...(socialSettings.postsEnabled ? styles.toggleActive : {})
              }}
              onClick={() => updateSettings({
                ...socialSettings,
                postsEnabled: !socialSettings.postsEnabled
              })}
            >
              <div style={{
                ...styles.toggleButton,
                ...(socialSettings.postsEnabled ? styles.toggleButtonActive : {})
              }} />
            </div>
          </div>
          
          <div style={styles.settingItem}>
            <span style={styles.settingLabel}>Likes Enabled</span>
            <div 
              style={{
                ...styles.toggle,
                ...(socialSettings.likesEnabled ? styles.toggleActive : {})
              }}
              onClick={() => updateSettings({
                ...socialSettings,
                likesEnabled: !socialSettings.likesEnabled
              })}
            >
              <div style={{
                ...styles.toggleButton,
                ...(socialSettings.likesEnabled ? styles.toggleButtonActive : {})
              }} />
            </div>
          </div>
          
          <div style={styles.settingItem}>
            <span style={styles.settingLabel}>Comments Enabled</span>
            <div 
              style={{
                ...styles.toggle,
                ...(socialSettings.commentsEnabled ? styles.toggleActive : {})
              }}
              onClick={() => updateSettings({
                ...socialSettings,
                commentsEnabled: !socialSettings.commentsEnabled
              })}
            >
              <div style={{
                ...styles.toggleButton,
                ...(socialSettings.commentsEnabled ? styles.toggleButtonActive : {})
              }} />
            </div>
          </div>
        </div>
        
        <div style={styles.actionsRow}>
          <button 
            style={{...styles.actionButton, ...styles.actionButtonSuccess}}
            onClick={() => setShowCreateModal(true)}
            disabled={!socialSettings.postsEnabled}
          >
            ➕ Test Create Post
          </button>
          <button 
            style={styles.actionButton}
            onClick={exportImages}
          >
            📥 Export Images
          </button>
          <button 
            style={{...styles.actionButton, ...styles.actionButtonPrimary}}
            onClick={() => {
              loadPosts(0, searchQuery);
              loadStatistics();
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{statistics.totalPosts}</div>
          <div style={styles.statLabel}>Total Posts</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{statistics.totalLikes}</div>
          <div style={styles.statLabel}>Total Likes</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{statistics.publicPosts}</div>
          <div style={styles.statLabel}>Public Posts</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{statistics.groupPosts}</div>
          <div style={styles.statLabel}>Group Posts</div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <input
          type="text"
          placeholder="Search posts..."
          value={searchQuery}
          onChange={handleSearch}
          style={styles.searchInput}
        />
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🧪 Test Create Post</h3>
              <button 
                style={styles.modalCloseButton}
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                style={styles.input}
                value={newPost.title}
                onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter post title..."
                maxLength={100}
              />
              <small style={{ color: '#64748b', fontSize: '12px' }}>
                {newPost.title.length}/100
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                value={newPost.description}
                onChange={(e) => setNewPost(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's on your mind..."
                maxLength={500}
              />
              <small style={{ color: '#64748b', fontSize: '12px' }}>
                {newPost.description.length}/500
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Image (Optional)</label>
              <input
                type="file"
                accept="image/*"
                style={styles.fileInput}
                onChange={handleImageSelect}
              />
              {newPost.selectedImage && (
                <div>
                  <img
                    src={URL.createObjectURL(newPost.selectedImage)}
                    alt="Selected"
                    style={styles.selectedImage}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    {newPost.selectedImage.name} ({(newPost.selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                  </small>
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  style={styles.checkboxInput}
                  checked={newPost.isGroupSpecific}
                  onChange={(e) => setNewPost(prev => ({ ...prev, isGroupSpecific: e.target.checked }))}
                />
                <span>Group-specific post</span>
              </label>
            </div>

            <div style={styles.modalActions}>
              <button
                style={styles.actionButton}
                onClick={() => setShowCreateModal(false)}
                disabled={createPostLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.actionButton, 
                  ...styles.actionButtonSuccess,
                  opacity: createPostLoading ? 0.6 : 1
                }}
                onClick={createPost}
                disabled={createPostLoading || !newPost.title.trim()}
              >
                {createPostLoading ? '⏳ Creating...' : '🚀 Create Test Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Posts List */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <div>Loading posts...</div>
        </div>
      ) : (
        <div style={styles.postsGrid}>
          <AnimatePresence>
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                style={styles.postCard}
                whileHover={styles.postCardHover}
              >
                <div 
                  style={styles.postHeader}
                  onClick={() => toggleExpanded(post.id)}
                >
                  <div style={styles.postMeta}>
                    <h3 style={styles.postTitle}>{post.title}</h3>
                    <div style={styles.postActions}>
                      <div
                        style={{...styles.actionIcon, ...styles.deleteIcon}}
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePost(post.id);
                        }}
                        title="Delete Post"
                      >
                        🗑️
                      </div>
                      <div
                        style={styles.actionIcon}
                        title={expandedPostId === post.id ? "Collapse" : "Expand"}
                      >
                        {expandedPostId === post.id ? "🔼" : "🔽"}
                      </div>
                    </div>
                  </div>
                  
                  <div style={styles.postInfo}>
                    <div style={styles.authorInfo}>
                      <span>👤 {post.authorName}</span>
                    </div>
                    <span>📅 {formatDate(post.createdAt)}</span>
                    <div
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(post.isGroupSpecific)
                      }}
                    >
                      {post.isGroupSpecific ? 'Group' : 'Public'}
                    </div>
                    <div style={styles.postStats}>
                      <div style={styles.statItem}>
                        <span>❤️ {post.likeCount || 0}</span>
                      </div>
                      {post.imagePath && (
                        <div style={styles.statItem}>
                          <span>🖼️ Image</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedPostId === post.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={styles.expandedContent}
                    >
                      {post.description && (
                        <div style={styles.postDescription}>
                          <strong>Description:</strong><br />
                          {post.description}
                        </div>
                      )}
                      
                  {post.imagePath && (
  <img
src={`${BASE_URL}/api/files/${post.imagePath}`}
    alt="Post"
    style={styles.postImage}
  />
)}
                      
                      <div style={styles.postDetails}>
                        <div style={styles.detailItem}>
                          <div style={styles.detailLabel}>Author Email</div>
                          <div style={styles.detailValue}>{post.authorEmail}</div>
                        </div>
                        <div style={styles.detailItem}>
                          <div style={styles.detailLabel}>Created</div>
                          <div style={styles.detailValue}>{formatDate(post.createdAt)}</div>
                        </div>
                        {post.updatedAt !== post.createdAt && (
                          <div style={styles.detailItem}>
                            <div style={styles.detailLabel}>Last Updated</div>
                            <div style={styles.detailValue}>{formatDate(post.updatedAt)}</div>
                          </div>
                        )}
                        <div style={styles.detailItem}>
                          <div style={styles.detailLabel}>Post ID</div>
                          <div style={styles.detailValue}>{post.id}</div>
                        </div>
                        {post.campId && (
                          <div style={styles.detailItem}>
                            <div style={styles.detailLabel}>Camp ID</div>
                            <div style={styles.detailValue}>{post.campId}</div>
                          </div>
                        )}
                        {post.groupId && (
                          <div style={styles.detailItem}>
                            <div style={styles.detailLabel}>Group ID</div>
                            <div style={styles.detailValue}>{post.groupId}</div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            style={{
              ...styles.pageButton,
              ...(currentPage === 0 ? styles.pageButtonDisabled : {})
            }}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (currentPage < 3) {
              pageNum = i;
            } else if (currentPage >= totalPages - 3) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                style={{
                  ...styles.pageButton,
                  ...(pageNum === currentPage ? styles.pageButtonActive : {})
                }}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum + 1}
              </button>
            );
          })}
          
          <button
            style={{
              ...styles.pageButton,
              ...(currentPage === totalPages - 1 ? styles.pageButtonDisabled : {})
            }}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </button>
        </div>
      )}

      {/* Add CSS animation for loading spinner */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SocialMediaAdmin;